// ============================================
// AtWorkDoorDetailPage - Chi tiết một cửa
// ============================================
//
// - Lịch sử event (mục 3.3) — poll mỗi 5s để cập nhật gần realtime (mục 4).
// - Audit log mở cửa remote theo cửa (mục 3.6).
// - Biểu đồ lượt mở theo ngày của riêng cửa (mục 3.4).

import { useI18n } from '../../i18n';
import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ADMIN_ROUTES,
  PERMISSIONS,
  ATWORK_EVENT_TYPES_OPENED,
  ATWORK_EVENT_TYPES_ENDED,
  ATWORK_COMMAND_STATUS_OPTIONS,
} from '@conai/shared-constants';
import type { AtWorkEvent, AtWorkDoorCommand } from '@conai/shared-types';
import { PageContainer, PageHeader } from '../../components/shared';
import { useHasPermission } from '../../stores/auth.store';
import {
  useAtWorkDoors,
  useAtWorkDoorEvents,
  useAtWorkDoorCommands,
  useAtWorkDailyStats,
  useProvideAccess,
} from '../../hooks';
import { ProvideAccessModal } from './components/ProvideAccessModal';
import { DailyAccessChart, type DailyAccessDatum } from './components/DailyAccessChart';

type Tab = 'events' | 'denied' | 'audit';

const OPENED = new Set<string>(ATWORK_EVENT_TYPES_OPENED);
const ENDED = new Set<string>(ATWORK_EVENT_TYPES_ENDED);

function EventTypeBadge({ type }: { type: string }) {
  const t = type ?? '';
  let cls = 'bg-slate-100 text-slate-700';
  if (t.startsWith('ACCESS_DENIED') || t.includes('ALARM')) cls = 'bg-red-100 text-red-700';
  else if (OPENED.has(t)) cls = 'bg-green-100 text-green-700';
  else if (ENDED.has(t)) cls = 'bg-blue-100 text-blue-700';
  else if (t.startsWith('DOOR_MODE')) cls = 'bg-amber-100 text-amber-700';

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>
      {t || '—'}
    </span>
  );
}

function CommandStatusBadge({ status }: { status: string }) {
  const { t } = useI18n();
  const opt = ATWORK_COMMAND_STATUS_OPTIONS.find((o) => o.value === status);
  const colorMap: Record<string, string> = {
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    orange: 'bg-amber-100 text-amber-700',
  };
  const cls = opt ? colorMap[opt.color] : 'bg-slate-100 text-slate-700';
  // Nhãn trong option là tiếng Anh cứng -> map sang i18n theo value.
  const labelKey: Record<string, string> = {
    PENDING: 'doorCmdProcessing',
    SUCCESS: 'doorCmdSuccess',
    FAILED: 'doorCmdFailed',
  };
  const label = labelKey[status] ? t(labelKey[status]) : (opt?.label ?? status);
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

function EventsTable({ data, isLoading }: { data: AtWorkEvent[]; isLoading: boolean }) {
  const { t } = useI18n();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-10">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
      </div>
    );
  }
  if (data.length === 0) {
    return <div className="p-10 text-center text-slate-500">{t('noEventsFound')}</div>;
  }
  return (
    <table className="min-w-full divide-y divide-slate-200">
      <thead className="bg-slate-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('eventTime')}</th>
          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('eventType')}</th>
          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('person')}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200">
        {data.map((e) => (
          <tr key={e.id} className="hover:bg-slate-50">
            <td className="px-6 py-4 text-sm text-slate-500">{new Date(e.timestamp).toLocaleString()}</td>
            <td className="px-6 py-4"><EventTypeBadge type={e.event_type} /></td>
            <td className="px-6 py-4 text-slate-700">{e.person_name ?? '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AuditTable({ data, isLoading }: { data: AtWorkDoorCommand[]; isLoading: boolean }) {
  const { t } = useI18n();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-10">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
      </div>
    );
  }
  if (data.length === 0) {
    return <div className="p-10 text-center text-slate-500">{t('noRemoteCommandsFound')}</div>;
  }
  return (
    <table className="min-w-full divide-y divide-slate-200">
      <thead className="bg-slate-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('eventTime')}</th>
          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('requester')}</th>
          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('source')}</th>
          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('reason')}</th>
          <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('status')}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200">
        {data.map((c) => (
          <tr key={c.id} className="hover:bg-slate-50">
            <td className="px-6 py-4 text-sm text-slate-500">{new Date(c.created_at).toLocaleString()}</td>
            <td className="px-6 py-4 text-slate-700">{c.requested_by_name ?? '-'}</td>
            <td className="px-6 py-4 text-slate-600">{c.source}</td>
            <td className="px-6 py-4 text-slate-600">{c.reason || '-'}</td>
            <td className="px-6 py-4 text-center">
              <CommandStatusBadge status={c.status} />
              {c.error_message && (
                <p className="mt-1 text-xs text-red-500">{c.error_message}</p>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AtWorkDoorDetailPage() {
  const { t } = useI18n();
  const { doorId = '' } = useParams<{ doorId: string }>();
  const navigate = useNavigate();
  const canOpen = useHasPermission(PERMISSIONS.ATWORK_OPEN);

  const [tab, setTab] = useState<Tab>('events');
  const [openModal, setOpenModal] = useState(false);

  const { data: doors } = useAtWorkDoors();
  const door = useMemo(
    () => doors?.find((d) => d.atwork_door_id === doorId),
    [doors, doorId]
  );

  // Poll mỗi 5s để cập nhật trạng thái cửa gần realtime (mục 4 - hướng polling)
  const { data: events, isLoading: eventsLoading } = useAtWorkDoorEvents(
    doorId,
    { limit: 100 },
    { refetchInterval: 5000 }
  );
  const { data: commands, isLoading: commandsLoading } = useAtWorkDoorCommands({ door_id: doorId, limit: 100 });
  const { data: dailyStats } = useAtWorkDailyStats();

  const provideAccess = useProvideAccess();

  const deniedEvents = useMemo(
    () => (events ?? []).filter((e) => (e.event_type ?? '').startsWith('ACCESS_DENIED')),
    [events]
  );

  const chartData: DailyAccessDatum[] = useMemo(() => {
    return (dailyStats ?? [])
      .filter((row) => row.door_id === doorId)
      .map((row) => ({ day: row.day, open_count: row.open_count, denied_count: row.denied_count }))
      .sort((a, b) => a.day.localeCompare(b.day));
  }, [dailyStats, doorId]);

  const handleConfirmOpen = (reason: string) => {
    provideAccess.mutate(
      { doorId, data: { reason: reason || undefined, source: 'ADMIN_WEB' } },
      { onSuccess: () => setOpenModal(false) }
    );
  };

  return (
    <PageContainer>
      <button
        onClick={() => navigate(ADMIN_ROUTES.ATWORK)}
        className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-700"
      >
        ← {t('backToDoorsList')}
      </button>

      <PageHeader
        title={door?.name ?? doorId}
        subtitle={door ? `${door.site_name}` : undefined}
        actions={
          <button
            onClick={() => setOpenModal(true)}
            disabled={!canOpen || (door ? !door.is_active : false)}
            title={!canOpen ? t('noPermissionToOpenDoor') : undefined}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('openTemporarily')}
          </button>
        }
      />

      {/* Chart riêng của cửa */}
      <div className="mt-6">
        <DailyAccessChart data={chartData} title={t('doorOpeningsOverTime')} />
      </div>

      {/* Tabs */}
      <div className="mt-6 inline-flex rounded-lg bg-slate-100 p-1">
        <button
          onClick={() => setTab('events')}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'events' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          {t('eventsHistory')}
        </button>
        <button
          onClick={() => setTab('denied')}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'denied' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          {t('deniedTab')}
          {deniedEvents.length > 0 && (
            <span className="ml-1.5 rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
              {deniedEvents.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('audit')}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'audit' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          {t('auditRemoteOpenings')}
        </button>
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
        {tab === 'events' ? (
          <EventsTable data={events ?? []} isLoading={eventsLoading} />
        ) : tab === 'denied' ? (
          <EventsTable data={deniedEvents} isLoading={eventsLoading} />
        ) : (
          <AuditTable data={commands ?? []} isLoading={commandsLoading} />
        )}
      </div>

      {openModal && door && (
        <ProvideAccessModal
          door={door}
          isSubmitting={provideAccess.isPending}
          onConfirm={handleConfirmOpen}
          onClose={() => setOpenModal(false)}
        />
      )}
    </PageContainer>
  );
}
